import React from 'react';
import {stringify} from 'querystring';
import FillScale from 'components/FillScale';
import OrientationScale from 'components/OrientationScale';
import {getHost, navigateTo} from 'utilities/URL';
import ExtraCardBehavior from '../shared/ExtraCardBehavior';
import FullScreenIframe from '../../../components/FullScreenIframe';
import FullScreenModalHeader from '../shared/FullScreenModalHeader';
import FullScreenButton from '../shared/FullScreenButton';
import PdpNotice from 'components/PdpNotice';
import Modal from 'components/Modal';
import {
  getEnvironment,
  selectAuthorizedAppDomains,
  selectIsPublic,
  selectIsFull,
} from 'state/environment/environmentSelectors';
import {connect} from 'react-redux';
import styles from './DomoAppDetails.css?module';
import {getBaseURL} from './DomoAppService';
import JsonRpc from '@statianzo/pmrpc';
import {LOCAL, REMOTE} from 'app/JsRpcConstants';
import {RpcContext} from 'components/RpcEndpoint';
import {default as Request, cancelRequest} from 'utilities/Request';

const parseJSONSafely = data => {
  try {
    return JSON.parse(data);
  } catch {
    return {};
  }
};

class DomoAppDetails extends React.Component {
  state = {isOpen: false, url: null};

  parsedUrl = new URL(window.location.href);
  appDataParam = this.parsedUrl.searchParams.get('appData');

  componentDidMount() {
    window.addEventListener('message', this.onMessage);
    this.setURL();
  }

  componentWillUnmount() {
    window.removeEventListener('message', this.onMessage);
    if (this.rpcPort && this.rpcPort.close) this.rpcPort.close();
    if (this.messagePort && this.messagePort.close) this.messagePort.close();
    cancelRequest(this.request);
  }

  setURL() {
    this.getURL().then(url => {
      if (this.state.url != url) {
        this.setState({url});
        this.rpc = null;
        this.rpcPort = null;
      }
    });
  }

  componentDidUpdate(prevProps) {
    this.checkIfFiltersChanged(prevProps);
  }

  checkIfFiltersChanged(prevProps) {
    if (this.anyPropUsedByURLOtherThanFilterHasChanged(prevProps)) {
      this.setURL();
      return;
    }

    if (
      prevProps.filters != this.props.filters &&
      (this.props.filters.length != 0 || prevProps.filters.length != 0)
    ) {
      if (!this.rpc) {
        this.setURL();
        return;
      }

      this.handleFilterChange(this.props.filters);
    }
  }

  anyPropUsedByURLOtherThanFilterHasChanged(prevProps) {
    const props = this.props;
    return (
      prevProps.pageId != props.pageId ||
      prevProps.card != props.card ||
      prevProps.environment != props.environment ||
      prevProps.size != props.size
    );
  }

  handleClientSubscribe = port => {
    if (!port) return;
    this.rpcPort = port;

    this.rpc = new JsonRpc({
      destination: port,
      source: port,
    });
    port.start();
  };

  handleFilterChange = filters => {
    this.rpc.call('filter', {filters}).then(
      ({isHandled}) => {
        if (!isHandled) this.setURL();
      },
      () => this.setURL()
    );
  };

  getURL() {
    const filters = this.props.filters;
    const options = {
      userId: this.props.environment.user.id,
      userName: this.props.environment.user.displayName,
      userEmail: (this.props.environment.user.detail || {}).email,
      customer: this.props.environment.customer,
      locale: this.props.environment.locale,
      environment: this.props.environment.environment,
      platform: 'mobile',
      size: this.props.size,
      dataControlFilters: JSON.stringify(this.props.dataControlFilters),
      analyzer: filters
        ? JSON.stringify(
            this.props.isPublic
              ? filters.map(f => {
                  const newFilter = Object.assign({}, f);
                  delete newFilter.dataSourceId;
                  return newFilter;
                })
              : filters
          )
        : '',
    };

    // public embed or private embed v2
    if (this.props.isPublic) {
      options.embedCode = this.props.cardId;
    }

    if (window.ENV && window.ENV.davinci) {
      options.davinci = true;
      options.view = this.props.viewId;
    }

    if (this.appDataParam) {
      options.appData = this.appDataParam;
    }

    if (this.props.pageId) {
      options.pageId = this.props.pageId;
    }

    const url = `${getBaseURL(
      this.props.cardId,
      this.props.pageId,
      this.props.card.domoapp.id,
      this.props.environment.domoappsDomain,
      this.props.isPublic
    )}?${stringify(options)}`;

    if (this.props.isPublic) {
      cancelRequest(this.request);
      this.request = Request.get(url, {responseType: 'blob'});
      return this.request.then(blob => {
        // DOMO-299459: Truncate the content type, because `text/html;chartset=utf-8` doesn't work in Safari 14.1 but `text/html` does.
        const [type] = blob.type.split(';');
        return URL.createObjectURL(new Blob([blob], {type}));
      });
    } else {
      return Promise.resolve(url);
    }
  }

  isHostSafe = hostname =>
    this.props.authorizedAppDomains.length === 0 ||
    this.props.authorizedAppDomains.includes(hostname);

  onPortMessage = e => {
    if (e.data.method) {
      switch (e.data.method) {
        case LOCAL.V1_ON_FRAME_SIZE_CHANGE:
          this.context.applyMethod(
            REMOTE.V1_ON_FRAME_SIZE_CHANGE,
            e.data.params
          );
          break;
      }
    }
  };

  handleJSAPISetup = port => {
    this.messagePort = port;
    this.messagePort.start();
    this.messagePort.onmessage = this.onPortMessage;
  };

  onMessage = e => {
    const domoappId = this.props.card.domoapp.id;
    if (!domoappId || e.origin.indexOf(domoappId) === -1) {
      return;
    }

    if (e.data && e.data.upgrade && e.ports && e.ports[0]) {
      this.handleJSAPISetup(e.ports[0]);
      return;
    }

    const message = parseJSONSafely(e.data);

    if (message.event === 'navigate') {
      const host = getHost(message.url);
      const isSafe = this.isHostSafe(host);

      if (!isSafe) return;

      navigateTo(message.url);
    } else if (message.event === 'subscribe') {
      this.handleClientSubscribe(e.ports[0]);
    }
  };

  handleFullScreenClick = () => {
    this.setState({isOpen: true});
  };

  handleRequestModalClose = () => {
    this.setState({isOpen: false});
  };

  render() {
    const {cardId, environment, isFull} = this.props;
    if (!environment) return null;

    const CardScale = isFull ? OrientationScale : FillScale;

    return (
      <div>
        <CardScale>
          {(width, height, hasComputed) =>
            hasComputed ? (
              <iframe
                title={this.props.card.title}
                className={styles.frame}
                style={{width, height}}
                src={this.state.url}
              />
            ) : null
          }
        </CardScale>
        <PdpNotice cardId={cardId} />
        {isFull ? (
          <ExtraCardBehavior>
            <FullScreenButton onClick={this.handleFullScreenClick} />
          </ExtraCardBehavior>
        ) : null}
        <Modal
          isOpen={this.state.isOpen}
          onRequestModalClose={this.handleRequestModalClose}
        >
          <FullScreenModalHeader onClick={this.handleRequestModalClose} />
          <FullScreenIframe
            title={this.props.card.title}
            src={this.state.url}
          />
        </Modal>
      </div>
    );
  }
}
DomoAppDetails.contextType = RpcContext;

export default connect(state => ({
  isPublic: selectIsPublic(state),
  environment: getEnvironment(state),
  authorizedAppDomains: selectAuthorizedAppDomains(state),
  isFull: selectIsFull(state),
}))(DomoAppDetails);
